import {
  APP_TIME_ZONE,
  formatZonedDayMonth,
  formatZonedTime,
  formatZonedWeekday,
  zonedParts,
} from "@/lib/appTime";
import { cn } from "@/lib/utils";
import type { NumberedRow } from "@/utils/schedule";

/**
 * Shared visual schedule (KC-363 follow-up).
 *
 * Weeks are rows, Mon–Sun are columns, and each assignment is a bar spanning its
 * open → due window. Rest gaps (exposed track) and partial first weeks fall out
 * of the geometry. Purely presentational — fed by `numberWindows()` and used in
 * both the Create modal (illustrative, "local" zone) and Manage schedule (real
 * assignments, app zone).
 */

const DAY_MS = 24 * 60 * 60 * 1000;
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

interface SchedulePreviewProps {
  rows: NumberedRow[];
  /**
   * Zone the window datetimes are read in. Defaults to the app zone; pass
   * "local" for the pre-creation preview whose windows are built in local time.
   */
  timeZone?: string;
  className?: string;
}

export function SchedulePreview({
  rows,
  timeZone = APP_TIME_ZONE,
  className,
}: SchedulePreviewProps) {
  if (rows.length === 0) {
    return (
      <p className={cn("py-4 text-sm text-gray-400", className)}>
        Pick a start date to preview the schedule.
      </p>
    );
  }

  // Group by derived week number (contiguous, 1-based).
  const byWeek = new Map<number, NumberedRow[]>();
  for (const row of rows) {
    const list = byWeek.get(row.week) ?? [];
    list.push(row);
    byWeek.set(row.week, list);
  }

  // Monday (as a UTC calendar date) of the earliest window's week — the anchor
  // for every week-row's date label. Built from zoned calendar parts so the
  // label is not shifted by the viewer's own timezone.
  const first = zonedParts(rows[0].start, timeZone);
  const firstMondayUtc = Date.UTC(first.year, first.month - 1, first.day - first.weekdayMon0);

  return (
    <div className={cn("select-none", className)}>
      <div className="mb-1.5 grid grid-cols-[5.5rem_1fr] gap-3">
        <div />
        <div className="grid grid-cols-7">
          {DOW.map((d, i) => (
            <span
              key={d}
              className={cn(
                "pb-0.5 text-center text-[10px] font-semibold uppercase tracking-wide",
                i >= 5 ? "text-emerald-700/70" : "text-gray-400"
              )}
            >
              {d}
            </span>
          ))}
        </div>
      </div>

      {[...byWeek.entries()].map(([week, weekRows]) => {
        const mondayUtc = new Date(firstMondayUtc + (week - 1) * 7 * DAY_MS);
        const sundayUtc = new Date(firstMondayUtc + ((week - 1) * 7 + 6) * DAY_MS);
        // Week 1 is "partial" when the cohort starts mid-week (first bar is not Monday).
        const partial = week === 1 && zonedParts(weekRows[0].start, timeZone).weekdayMon0 !== 0;

        return (
          <div key={week} className="grid grid-cols-[5.5rem_1fr] items-center gap-3 py-1">
            <div className="text-right">
              <div className="text-[13px] font-semibold text-gray-800">Week {week}</div>
              <div className="text-[11px] tabular-nums text-gray-400">
                {formatZonedDayMonth(mondayUtc, "UTC")} – {formatZonedDayMonth(sundayUtc, "UTC")}
              </div>
              {partial && (
                <span className="mt-1 inline-block rounded-full border border-amber-200 bg-amber-50 px-1.5 py-px text-[9px] font-bold uppercase tracking-wide text-amber-700">
                  partial
                </span>
              )}
            </div>

            <div
              className="relative h-11 overflow-hidden rounded-lg bg-emerald-50 ring-1 ring-inset ring-emerald-100"
              style={{
                backgroundImage:
                  "repeating-linear-gradient(to right, transparent 0, transparent calc(100%/7 - 1px), rgba(4,120,87,0.12) calc(100%/7 - 1px), rgba(4,120,87,0.12) calc(100%/7))",
              }}
            >
              {weekRows.map((row) => {
                const parts = zonedParts(row.start, timeZone);
                const leftDays = parts.weekdayMon0 + parts.minutesOfDay / 1440;
                const durationDays = (row.deadline.getTime() - row.start.getTime()) / DAY_MS;
                const rightDays = Math.min(leftDays + durationDays, 7);
                const leftPct = (leftDays / 7) * 100;
                const widthPct = Math.max((rightDays - leftDays) / 7, 0) * 100;
                const narrow = widthPct < 16;
                const range = `${formatZonedWeekday(row.start, timeZone)} ${formatZonedTime(
                  row.start,
                  timeZone
                )} → ${formatZonedWeekday(row.deadline, timeZone)} ${formatZonedTime(
                  row.deadline,
                  timeZone
                )}`;
                return (
                  <div
                    key={row.wp}
                    className={cn(
                      "absolute inset-y-1 flex flex-col justify-center overflow-hidden rounded-md px-2 text-white shadow-sm ring-1 ring-white/10",
                      row.wp === 1 ? "bg-emerald-900" : "bg-emerald-700",
                      narrow && "px-1"
                    )}
                    style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                    title={`WP${row.wp} · ${formatZonedDayMonth(row.start, timeZone)} — ${range}`}
                  >
                    <span className="whitespace-nowrap text-[11px] font-bold leading-tight">
                      WP{row.wp}
                    </span>
                    {!narrow && (
                      <span className="whitespace-nowrap text-[10px] leading-tight tabular-nums text-white/80">
                        {range}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 border-t border-dashed border-gray-200 pt-2.5 text-[11px] text-gray-500">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-5 rounded-sm bg-emerald-900" /> WP1 (first)
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-5 rounded-sm bg-emerald-700" /> Assignment window
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-5 rounded-sm bg-emerald-50 ring-1 ring-inset ring-emerald-100" />{" "}
          Rest / gap
        </span>
      </div>
    </div>
  );
}
